// 用途: Firebase Crashlytics の薄いラッパーサービス。ネイティブ環境での有効化、未捕捉エラー捕捉、ユーザーID紐付け、ログ/例外送信を提供します。

import { Injectable } from '@angular/core';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { environment } from '@environments/environment';

export type CrashLogLevel = 'debug' | 'info' | 'warn' | 'error';

@Injectable({providedIn: 'root'})
export class FirebaseCrashlyticsService {
  private enabled = false;
  private handlersAttached = false;

  async setEnabled(enabled: boolean): Promise<void> {
    this.enabled = enabled && environment.firebase.enabled;
    if (!environment.firebase.enabled) {
      return;
    }
    try {
      await FirebaseCrashlytics.setEnabled({enabled: this.enabled});
    } catch {
      // Web 実行など非対応環境では無視
    }
  }

  async setUserId(userId: string): Promise<void> {
    if (!this.enabled) return;
    try {
      await FirebaseCrashlytics.setUserId({userId});
    } catch {
      // 非対応環境では無視
    }
  }

  async setCustomKey(key: string, value: string | number | boolean): Promise<void> {
    if (!this.enabled) return;
    try {
      let type: 'string' | 'boolean' | 'long' | 'double' | 'int' | 'float';
      if (typeof value === 'string') {
        type = 'string';
      } else if (typeof value === 'boolean') {
        type = 'boolean';
      } else {
        // number: 整数は int（32bit 範囲内）/ それ以外は double、32bit 超過整数は long へ
        const n = value as number;
        if (Number.isInteger(n)) {
          type = n >= -2147483648 && n <= 2147483647 ? 'int' : 'long';
        } else {
          type = 'double';
        }
      }
      await FirebaseCrashlytics.setCustomKey({key, type, value});
    } catch {
      // 非対応環境では無視
    }
  }

  async log(message: string, level: CrashLogLevel = 'info'): Promise<void> {
    if (!this.enabled) return;
    try {
      // ログレベルをカスタムキーで付与
      await FirebaseCrashlytics.setCustomKey({key: 'logLevel', type: 'string', value: level});
      await FirebaseCrashlytics.log({message});
    } catch {
      // 非対応環境では無視
    }
  }

  async recordException(error: unknown, level: CrashLogLevel = 'error'): Promise<void> {
    if (!this.enabled) return;
    const message =
      error instanceof Error ? `${ error.name }: ${ error.message }\n${ error.stack ?? '' }` : String(error);
    try {
      await FirebaseCrashlytics.setCustomKey({key: 'logLevel', type: 'string', value: level});
      await FirebaseCrashlytics.recordException({message});
    } catch {
      // 非対応環境では無視
    }
  }

  // 意図的にクラッシュさせて動作確認したい場合
  async crash(message: string = 'Test crash'): Promise<void> {
    if (!this.enabled) return;
    try {
      console.log('#### FirebaseCrashlyticsService.crash called');
      await FirebaseCrashlytics.crash({message});
    } catch {
      // 非対応環境では無視
    }
  }

  // 未捕捉エラーを Crashlytics に集約（重複登録は避ける）
  attachGlobalHandlers(): void {
    if (typeof window === 'undefined') return;
    if (this.handlersAttached) return;
    this.handlersAttached = true;

    const onError = (event: ErrorEvent) => {
      void this.recordException(event.error ?? event.message, 'error');
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      void this.recordException(event.reason ?? 'Unhandled promise rejection', 'error');
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
  }
}
